import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Barcode, ChevronRight, Copy, ScanText, Search, Star, Zap } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { BotonEnlace } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { leerSettings } from '@/db/schema';
import { leerComidasRecientes } from '@/db/comidas';
import { leerPlatillos } from '@/db/platillos';
import type { Dish, EstadoCoccion, Food } from '@/db/types';
import { fechaDelDia } from '@/domain/hoy';
import { buscarAlimentos, recientesDe, usosDe, type Candidato } from '@/domain/busqueda';
import {
  NOMBRE_TIEMPO,
  estadoDe,
  formatearKcal,
  textoCantidad,
  tiempoSegunHora,
  type AlimentoBase,
} from '@/domain/nutricion';
import { formatearNumero } from '@/domain/numbers';
import { horaActual } from '@/domain/time';
import type { ResolverIngrediente } from '@/domain/platillos';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Etiqueta, EtiquetaEstado, esTiempoComida, fechaDeParametro } from './comunes';
import { kcalPorRacion, useResolverIngredientes } from './ingredientes';

interface CandidatoAlimento extends Candidato {
  origen: 'base' | 'propio' | 'platillo';
  id: string;
  estado: EstadoCoccion | null;
  detalle: string;
}

function detalleDePropio(f: Food): string {
  if (f.referencia.tipo === 'por100g') return `${formatearKcal(f.nutrientes.kcal)} por 100 g`;
  const p = f.referencia.porcion;
  const gramos = p.gramos === null ? '' : ` (${formatearNumero(p.gramos, { decimales: 0 })} g)`;
  return `${formatearKcal(f.nutrientes.kcal)} por ${p.nombre}${gramos}`;
}

function candidatosDe(
  base: readonly AlimentoBase[],
  favoritosBase: readonly string[],
  propios: readonly Food[],
  platillos: readonly Dish[],
  resolver: ResolverIngrediente,
): CandidatoAlimento[] {
  return [
    ...platillos
      .filter((p) => !p.archivado)
      .map((p) => ({
        clave: `platillo:${p.id}`,
        origen: 'platillo' as const,
        id: p.id,
        nombre: p.nombre,
        sinonimos: [],
        favorito: p.favorito,
        estado: null,
        detalle: kcalPorRacion(p, resolver),
      })),
    ...propios
      .filter((f) => !f.archivado)
      .map((f) => ({
        clave: `propio:${f.id}`,
        origen: 'propio' as const,
        id: f.id,
        nombre: f.nombre,
        sinonimos: [],
        favorito: f.favorito,
        estado: estadoDe(f),
        detalle: detalleDePropio(f),
      })),
    ...base.map((a) => ({
      clave: `base:${a.id}`,
      origen: 'base' as const,
      id: a.id,
      nombre: a.nombre,
      sinonimos: a.sinonimos,
      favorito: favoritosBase.includes(a.id),
      estado: a.estado,
      detalle: `${formatearKcal(a.por100g.kcal)} por 100 g`,
    })),
  ];
}

/**
 * Agregar a un tiempo de comida (Req. 18.1 a 18.3, 19.5, 19.6): buscador sin
 * acentos con sinónimos, favoritos y recientes primero, registro rápido y
 * alimento nuevo desde su etiqueta. Todo funciona sin conexión.
 */
export function AgregarPage() {
  const hoy = useFechaHoy();
  const [parametros] = useSearchParams();
  const fecha = fechaDeParametro(parametros.get('fecha'), hoy);
  const tiempoParametro = parametros.get('tiempo');
  const [horaAlAbrir] = useState(() => horaActual());
  const tiempo = esTiempoComida(tiempoParametro) ? tiempoParametro : tiempoSegunHora(horaAlAbrir);
  const destino = `fecha=${fecha}&tiempo=${tiempo}`;

  const [consulta, setConsulta] = useState('');
  const { base, propios, resolver } = useResolverIngredientes();
  const favoritosBase = useLiveQuery(async () => (await leerSettings()).favoritosBase ?? [], []);
  const platillos = useLiveQuery(() => leerPlatillos(), []);
  const recientes = useLiveQuery(() => leerComidasRecientes(hoy), [hoy]);

  const candidatos = useMemo(
    () =>
      base.estado === 'lista' && favoritosBase !== undefined && propios !== undefined && platillos !== undefined && resolver !== null
        ? candidatosDe(base.modulo.ALIMENTOS_BASE, favoritosBase, propios, platillos, resolver)
        : null,
    [base, favoritosBase, propios, platillos, resolver],
  );
  const usos = useMemo(() => usosDe(recientes ?? [], hoy), [recientes, hoy]);

  const volver = { a: fecha === hoy ? '/comida' : `/comida?fecha=${fecha}`, texto: 'Comida', etiqueta: 'Volver a Comida' };
  const nombreTiempo = NOMBRE_TIEMPO[tiempo].toLowerCase();

  const resultados = candidatos === null || consulta.trim() === '' ? null : buscarAlimentos(consulta, candidatos, usos, hoy);
  const favoritos = candidatos?.filter((c) => c.favorito) ?? [];
  const ultimos = recientesDe(recientes ?? [], 8);

  return (
    <div>
      <PageHeader antetitulo={fechaDelDia(fecha)} titulo={`Agregar a ${nombreTiempo}.`} volver={volver} />

      <label htmlFor="buscar-alimento" className="sr-only">
        Buscar alimento
      </label>
      <div className="relative">
        <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-texto-suave" />
        <input
          id="buscar-alimento"
          type="search"
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          placeholder="Tortilla, huevo, plátano…"
          autoComplete="off"
          enterKeyHint="search"
          className="min-h-12 w-full rounded-[13px] border border-borde-fuerte bg-superficie py-[11px] pl-10 pr-3 text-base text-texto placeholder:text-texto-suave"
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <BotonEnlace to={`/comida/agregar/rapido?${destino}`} variante="secundario" icono={Zap}>
          Registro rápido
        </BotonEnlace>
        <BotonEnlace
          to={`/comida/mis-alimentos/nuevo?volver=${encodeURIComponent(`/comida/agregar?${destino}`)}`}
          variante="secundario"
          icono={ScanText}
        >
          Desde etiqueta
        </BotonEnlace>
        {/* Req. 18.6: el mismo tiempo de comida de otro día */}
        <BotonEnlace to={`/comida/copiar?${destino}`} variante="secundario" icono={Copy}>
          Copiar de otro día
        </BotonEnlace>
        {/* Producto empaquetado: la única pantalla que usa internet (0.13.0) */}
        <BotonEnlace
          to={`/comida/codigo?volver=${encodeURIComponent(`/comida/agregar?${destino}`)}`}
          variante="secundario"
          icono={Barcode}
        >
          Código de barras
        </BotonEnlace>
      </div>

      {base.estado === 'error' && (
        <div className="mt-5">
          <Banner tono="alerta" titulo="No se pudo abrir la base de alimentos">
            Cierra y vuelve a abrir la app. Mientras tanto puedes usar el registro rápido.
          </Banner>
        </div>
      )}

      {resultados !== null ? (
        <section className="mt-6" aria-labelledby="titulo-resultados">
          <h2 id="titulo-resultados" className="antetitulo">
            {resultados.length === 0 ? 'Sin resultados' : `${resultados.length} ${resultados.length === 1 ? 'resultado' : 'resultados'}`}
          </h2>
          {resultados.length === 0 ? (
            <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
              “{consulta.trim()}” no está en la base, en tus alimentos ni en tus platillos. Haz un
              registro rápido o captúralo desde su etiqueta.
            </p>
          ) : (
            <ListaCandidatos candidatos={resultados} destino={destino} />
          )}
        </section>
      ) : (
        <>
          {favoritos.length > 0 && (
            <section className="mt-6" aria-labelledby="titulo-favoritos">
              <h2 id="titulo-favoritos" className="antetitulo">
                Favoritos
              </h2>
              <ListaCandidatos candidatos={favoritos} destino={destino} />
            </section>
          )}
          <section className="mt-6" aria-labelledby="titulo-recientes">
            <h2 id="titulo-recientes" className="antetitulo">
              Recientes
            </h2>
            {ultimos.length === 0 ? (
              <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
                Lo que registres aparecerá aquí para repetirlo en un toque. Busca arriba entre{' '}
                {base.estado === 'lista' ? base.modulo.ALIMENTOS_BASE.length : 'los'} alimentos de la base.
              </p>
            ) : (
              <ul className="divide-y divide-borde">
                {ultimos.map((e) => {
                  const enlace =
                    e.origen.tipo === 'rapido'
                      ? `/comida/agregar/rapido?${destino}&desde=${e.id}`
                      : `/comida/agregar/registro/${e.id}?${destino}`;
                  return (
                    <li key={e.id}>
                      <Link to={enlace} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
                        <span className="grid min-w-0 flex-1 gap-1">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[14px] font-medium text-texto">{e.nombre}</span>
                            <EtiquetaEstado estado={estadoDe(e)} />
                          </span>
                          <span className="text-[12px] text-texto-suave">
                            {[textoCantidad(e) ?? 'Registro rápido', formatearKcal(e.valores.kcal)].join(' · ')}
                          </span>
                        </span>
                        <ChevronRight className="size-4 text-texto-suave" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function ListaCandidatos({ candidatos, destino }: { candidatos: readonly CandidatoAlimento[]; destino: string }) {
  return (
    <ul className="divide-y divide-borde">
      {candidatos.map((c) => (
        <li key={c.clave}>
          <Link to={`/comida/agregar/${c.origen}/${c.id}?${destino}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-[14px] font-medium text-texto">{c.nombre}</span>
                <EtiquetaEstado estado={c.estado} />
                {c.origen === 'propio' && <Etiqueta>Mío</Etiqueta>}
                {c.origen === 'platillo' && <Etiqueta>Platillo</Etiqueta>}
                {c.favorito && (
                  <>
                    <Star aria-hidden="true" className="size-3.5 fill-current text-marca" />
                    <span className="sr-only">Favorito</span>
                  </>
                )}
              </span>
              <span className="text-[12px] text-texto-suave">{c.detalle}</span>
            </span>
            <ChevronRight className="size-4 text-texto-suave" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
